'use client';

import { useEffect, useState } from 'react';
import { EditModal } from '@/components/ui/edit-modal';
import { PlayersPerTeamField } from '@/features/tournament-wizard/blocks';
import { sports } from '@/types';

interface EditPlayersPerTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlayersPerTeam: number;
  /** Must follow the same rules as the wizard (e.g. padel is always 2). */
  sportUid?: string;
  /** Fallback when `uid` is empty or another format; useful to detect padel. */
  sportName?: string;
  onSave: (playersPerTeam: number) => Promise<boolean>;
  isLoading: boolean;
}

/** Same business rule as `padelTeamConfigStepSchema`: padel = always 2 players per team. */
function isPadelSport(sportUid: string | undefined, sportName: string | undefined) {
  const u = sportUid?.trim().toLowerCase();
  if (u === 'padel' || u === 'pádel') return true;
  const n = sportName?.trim().toLowerCase() ?? '';
  return n.includes('padel') || n.includes('pádel');
}

function playersPerTeamConfig(sportUid: string | undefined, sportName: string | undefined) {
  if (isPadelSport(sportUid, sportName)) {
    return {
      minPlayers: 2,
      maxPlayers: 2,
      labelStyle: 'jugadores' as const,
      cardTitle: 'Jugadores por equipo',
    };
  }

  const uid = sportUid?.trim().toLowerCase();
  switch (uid) {
    case 'tenis':
    case 'squash':
      return {
        minPlayers: 1,
        maxPlayers: 2,
        labelStyle: 'singlesDoubles' as const,
        cardTitle: 'Modalidad',
      };
    default: {
      const key = uid ?? '';
      const max = Math.max(1, sports[key]?.maxPlayers ?? 2);
      return {
        minPlayers: 1,
        maxPlayers: max,
        labelStyle: 'jugadores' as const,
        cardTitle: 'Jugadores por equipo',
      };
    }
  }
}

export function EditPlayersPerTeamModal({
  isOpen,
  onClose,
  currentPlayersPerTeam,
  sportUid,
  sportName,
  onSave,
  isLoading,
}: EditPlayersPerTeamModalProps) {
  const [playersPerTeam, setPlayersPerTeam] = useState(currentPlayersPerTeam);

  const cfg = playersPerTeamConfig(sportUid, sportName);

  useEffect(() => {
    if (!isOpen) return;
    const clamped = Math.max(
      cfg.minPlayers,
      Math.min(currentPlayersPerTeam, cfg.maxPlayers),
    );
    setPlayersPerTeam(clamped);
  }, [isOpen, currentPlayersPerTeam, cfg.minPlayers, cfg.maxPlayers]);

  const handleSave = async () => {
    await onSave(playersPerTeam);
  };

  return (
    <EditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar Jugadores por Equipo"
      onSave={handleSave}
      isLoading={isLoading}
    >
      <div className="py-4">
        <PlayersPerTeamField
          value={playersPerTeam}
          onChange={setPlayersPerTeam}
          minPlayers={cfg.minPlayers}
          maxPlayers={cfg.maxPlayers}
          labelStyle={cfg.labelStyle}
          cardTitle={cfg.cardTitle}
          wrapInCard={false}
          disabled={isLoading}
        />
      </div>
    </EditModal>
  );
}
